import React, { Fragment, useState, useEffect, useRef } from "react";
import {
  Table,
  Card,
  CardTitle,
  Button,
  Col,
  Input,
  FormGroup,
  Row,
  Badge,
} from "reactstrap";
import { CUSTOMER_DETAILS_URL } from "../../utils/APIEndpoints";
import { authHeader } from "../../utils/functions/CoreFunctions";
import API from "../../redux/actions/API";
import {
  getAlert,
  SingleStringFirstLetterCaps,
  getCurrentAgencyId,
  getDateString,
  getAlertToast,
  dateTimeFormat,
  amountFormat,
} from "../../utils/functions/CustomFunctions";
import CustomTableLoader from "../common/CustomTableLoader";
import CustomTableEmptyRecord from "../common/CustomTableEmptyRecord";
import CustomTableHeader from "../common/CustomTableHeader";
import { useParams, Link } from "react-router-dom";
import Pagination from "react-js-pagination";
import CustomViewButton from "../common/CustomViewButton";
import AdditionalDocuments from "./additionalDocuments";
import { debounce } from "underscore";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import DatePicker from "react-datepicker";
import Swal from "sweetalert2";
import { CUSTOMERS_ROUTE_PATH } from "../../utils/AppRoutes";

const paymentStatusColor = {
  Success: "primary",
  Failed: "danger",
  Pending: "warning",
};

//Table Columns
const tableColumns = [
  { title: "Payment Date" },
  { title: "Customer Name" },
  { title: "Document Name" },
  { title: "Document Fee" },
  { title: "Invoice" },
  { title: "Payment Type" },
  { title: "Payment Status" },
  { title: "Completed Form" },
  { title: "Additional Documents" },
];

const Customers = (props) => {
  //State
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [additionalDocuments, setAdditionalDocuments] = useState({});
  const viewToggle = () => setViewModal(!viewModal);
  let { email } = useParams();
  const [filter, setFilter] = useState({
    invoiceId: "",
    serviceName: "",
    status: "",
    paymentStartDate: "",
    paymentEndDate: "",
    paymentStatus: "",
    count: 10,
  });

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const startDateChange = (date) => {
    setStartDate(date || "");
    let paymentStartDate = date ? moment(date).format("MM/DD/YYYY") : "";
    setFilter({ ...filter, ["paymentStartDate"]: paymentStartDate });
  };

  const endDateChange = (date) => {
    setEndDate(date || "");
    let paymentEndDate = date ? moment(date).format("MM/DD/YYYY") : "";
    setFilter({ ...filter, ["paymentEndDate"]: paymentEndDate });
  };

  //Debounce
  const delayedQuery = useRef(
    debounce(
      (filter) =>
        getGridData({
          ...filter,
          offset: currentPage,
          agencyId: parseInt(getCurrentAgencyId()),
          email: email,
        }),
      500
    )
  ).current;

  //Onchange filter
  const changeFilterField = ({ target: { name, value } }) => {
    setFilter({ ...filter, [name]: value });
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setFilter((val) => ({
      ...val,
      invoiceId: "",
      serviceName: "",
      status: "",
      paymentStartDate: "",
      paymentEndDate: "",
      paymentStatus: "",
    }));
  };

  const viewAdditionalDocuments = async (documents, e) => {
    setAdditionalDocuments(JSON.parse(documents));
    setViewModal(true);
  };

  const onPageChanged = (page) => {
    setCurrentPage(page - 1);
    getGridData({
      ...filter,
      offset: page - 1,
      agencyId: parseInt(getCurrentAgencyId()),
      email: email,
    });
  };

  //Get Grid Data
  const getGridData = async (queryParams) => {
    try {
      setLoading(true);
      const options = {
        method: "GET",
        url: CUSTOMER_DETAILS_URL,
        headers: authHeader(),
        params: queryParams,
      };
      const response = await API(options);
      const { status, data: initialResponse = [], totalCount } = response.data;
      if (status === "Success") {
        setTotalRecords(totalCount);
        let dataToDis = initialResponse.map((record) => {
          let { firstName, lastName, paymentDate, serviceAmount } = record;
          let formattedData = {
            firstName: SingleStringFirstLetterCaps(firstName),
            lastName: SingleStringFirstLetterCaps(lastName),
            paymentDate: paymentDate
              ? dateTimeFormat(paymentDate, "MM/DD/YYYY hh:mm A")
              : "",
            serviceAmount: amountFormat(serviceAmount),
          };
          let dataToReturn = { ...record, ...formattedData };
          return dataToReturn;
        });
        setGridData(dataToDis);
      }
    } catch (error) {
      if (error.response) {
        let { data } = error.response;
        Swal.fire(getAlertToast("error", data.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRowPerPage = ({ target: { value, name } }) => {
    setCurrentPage(0);
    setFilter((va) => ({ ...va, [name]: value }));
  };

  useEffect(() => {
    delayedQuery(filter);
  }, [filter]);

  const { invoiceId, serviceName, status, paymentDate, paymentStatus } = filter;

  return (
    <Fragment>
      <Card body className="mt-5">
        <div className="tableTitleBg mb-4">
          <CardTitle className=" " style={{ margin: "0" }}>
            {" "}
            Customer Details: <b>{email}</b>
          </CardTitle>
          <Link to={CUSTOMERS_ROUTE_PATH}>
            <Button className="f-r" color="primary">
              <i className="fas fa-arrow-left"></i> Back
            </Button>
          </Link>
          <i
            className="fas fa-filter"
            style={{
              float: "right",
              margin: "0px 7px 3px 16px",
              fontSize: "19px",
              cursor: "pointer",
            }}
            onClick={filterToggle}
          ></i>
        </div>
        {isFilterOpen && (
          <Row form className="mb-4" style={{ marginTop: "1rem" }}>
            <Col md={3}>
              <FormGroup>
                <Input
                  type="text"
                  name="serviceName"
                  placeholder="Document Name"
                  onChange={changeFilterField}
                  value={serviceName}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Input
                  type="text"
                  name="invoiceId"
                  placeholder="Invoice"
                  onChange={changeFilterField}
                  value={invoiceId}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <DatePicker
                  style={{ zIndex: "9999 !important", width: "100%" }}
                  selected={startDate}
                  onChange={startDateChange}
                  dateFormat="MM/dd/yyy"
                  maxDate={endDate}
                  placeholderText="Start Date"
                  className="form-control mb-3"
                  width="100%"
                  popperPlacement="bottom"
                  popperModifiers={{
                    flip: {
                      behavior: ["bottom"],
                    },
                    preventOverflow: {
                      enabled: false,
                    },
                    hide: {
                      enabled: false,
                    },
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <DatePicker
                  style={{ zIndex: "9999 !important", width: "100%" }}
                  selected={endDate}
                  onChange={endDateChange}
                  dateFormat="MM/dd/yyy"
                  placeholderText="End Date"
                  className="form-control mb-2"
                  minDate={startDate}
                  // width='100%'
                  popperPlacement="bottom"
                  popperModifiers={{
                    flip: {
                      behavior: ["bottom"],
                    },
                    preventOverflow: {
                      enabled: false,
                    },
                    hide: {
                      enabled: false,
                    },
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={1} />
            <Col md={3}>
              <FormGroup>
                <Input
                  type="select"
                  name="paymentStatus"
                  placeholder="Status"
                  onChange={changeFilterField}
                  value={paymentStatus}
                >
                  <option value={""}>-- Show All Status --</option>
                  <option value={"Pending"}>Pending</option>
                  <option value={"Success"}>Success</option>
                  <option value={"Failed"}>Failed</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={2}>
              <div style={{ marginTop: "4px" }}>
                <Button color="danger" onClick={handleReset}>
                  Reset
                </Button>
              </div>
            </Col>
          </Row>
        )}
        <Table responsive hover>
          {/* Table Header */}
          <CustomTableHeader tableColumns={tableColumns} />
          {/* Table Header */}
          <tbody>
            {loading ? (
              // Table Loader
              <CustomTableLoader columnsCount={tableColumns.length} />
            ) : (
              // Table Loader
              <Fragment>
                {!gridData.length ? (
                  //  Empty Record
                  <CustomTableEmptyRecord columnsCount={tableColumns.length} />
                ) : (
                  //  Empty Record
                  <Fragment>
                    {gridData.map((record, key) => (
                      <tr key={`record_key_${key}`}>
                        <td style={{ width: "100px" }}>{record.paymentDate}</td>
                        <td>
                          {record.firstName} {record.lastName}
                        </td>
                        <td>{record.serviceName}</td>
                        <td>{record.serviceAmount}</td>
                        <td>#{record.invoiceId}‎</td>
                        <td>{record.paymentType}</td>
                        <td>
                          <Badge
                            color={paymentStatusColor[record.paymentStatus]}
                          >
                            {record.paymentStatus}
                          </Badge>
                        </td>
                        <td style={{ width: "100px", textAlign: "center" }}>
                          {record.documentUrl ? (
                            <a href={record.documentUrl} target="_blank">
                              <Button className="action-btn" color="success">
                                <span className="fas fa-download"></span>
                              </Button>
                            </a>
                          ) : (
                            ""
                          )}
                        </td>
                        <td style={{ width: "100px", textAlign: "center" }}>
                          {/* Custom View Button  */}
                          {record.additionalDocuments ? (
                            <CustomViewButton
                              keyvalue={key}
                              onClick={(e) =>
                                viewAdditionalDocuments(
                                  record.additionalDocuments,
                                  e
                                )
                              }
                            />
                          ) : (
                            ""
                          )}
                          {/* Custom View Button  */}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                )}
              </Fragment>
            )}
          </tbody>
        </Table>
        {gridData.length ? (
          <div className="d-flex py-4 justify-content-end space_between">
            <div className="space_between">
              <div>
                <p className="per_page">Records/Page</p>
              </div>
              <div>
                <FormGroup>
                  <Input
                    className="pagi_drop"
                    type="select"
                    name="count"
                    onChange={handleRowPerPage}
                    value={filter.count}
                  >
                    <option value={""}>Show All</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Input>
                </FormGroup>
              </div>
            </div>
            {filter.count && (
              <div>
                <Pagination
                  itemClass="page-item"
                  linkClass="page-link"
                  activePage={currentPage + 1}
                  itemsCountPerPage={filter.count}
                  totalItemsCount={totalRecords}
                  onChange={onPageChanged}
                />
              </div>
            )}
          </div>
        ) : (
          ""
        )}
      </Card>
      {/* View Dialog */}
      {viewModal && (
        <AdditionalDocuments
          data={additionalDocuments}
          viewToggle={viewToggle}
          viewModal={viewModal}
        />
      )}
      {/* View Dialog */}
    </Fragment>
  );
};

export default Customers;
